/**
 * BybitKeyBanner — global page-top strip that surfaces critical Bybit
 * key issues on every screen (not just the dashboard).
 *
 * Renders ONLY when there's an actual alert we need the user to fix:
 *   – key invalid           → red,    "trading paused"
 *   – key expires in ≤ 7d   → amber, day countdown
 *
 * Everything else (healthy, no-keys-yet, balance changes) is intentionally
 * silent here — the BybitAccountCard on dashboard already covers them.
 * The point of a global banner is "you can't miss this", so we keep its
 * trigger surface narrow.
 *
 * Shares the `useBybitStatus` query with the card → react-query dedupes.
 */
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Clock, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useBybitStatus } from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'

// localStorage key — banner can be dismissed for the current calendar
// day so we don't pester the user every page nav. Telegram + dashboard
// card remain visible, so dismissing is safe.
const DISMISS_KEY = 'pulsar.bybit-banner.dismissed-on'

function todayStamp(): string {
  return new Date().toISOString().slice(0, 10)
}

export function BybitKeyBanner() {
  const { t } = useTranslation('app')
  const { data } = useBybitStatus()
  const [dismissed, setDismissed] = useState<string | null>(null)

  useEffect(() => {
    setDismissed(localStorage.getItem(DISMISS_KEY))
  }, [])

  if (!data) return null
  if (!data.has_api_keys) return null  // no creds = nothing to warn about
  if (dismissed === todayStamp()) return null

  // Pick the most severe alert; server returns 'red' (key_invalid)
  // before 'yellow' (key_expiring), so [0] is the worst case.
  const alert = data.alerts[0]
  if (!alert) return null

  const isRed = alert.severity === 'red'
  const days = alert.days_until_expiry

  let message: string
  if (alert.type === 'key_invalid') {
    message = t('bybit.banner.expired')
  } else if (days != null && days < 1) {
    message = t('bybit.banner.expiring_one')
  } else {
    message = t('bybit.banner.expiring', { days: Math.floor(days ?? 0) })
  }

  const onDismiss = () => {
    const stamp = todayStamp()
    localStorage.setItem(DISMISS_KEY, stamp)
    setDismissed(stamp)
  }

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-5 py-2 text-sm border-b border-line',
        isRed
          ? 'bg-rose/15 text-rose'
          : 'bg-amber/15 text-amber',
      )}
      role="alert"
    >
      {isRed ? <AlertTriangle size={16} /> : <Clock size={16} />}
      <span className="flex-1">{message}</span>
      <Link
        to="/app/settings"
        className="underline underline-offset-2 hover:brightness-110 font-medium"
      >
        {t('bybit.banner.fix')}
      </Link>
      <button
        onClick={onDismiss}
        className="opacity-70 hover:opacity-100"
        aria-label="dismiss"
        title="dismiss"
      >
        <X size={14} />
      </button>
    </div>
  )
}
